import type { CustomCellRendererProps } from 'ag-grid-react';
import { type Href, Link } from 'expo-router';
import { Badge } from 'primereact/badge';
import { Chip } from 'primereact/chip';
import {
    confirmDialog,
    type ConfirmDialogProps,
} from 'primereact/confirmdialog';
import React, { memo, useMemo } from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    CheckIcon,
    CopyIcon,
    EditIcon,
    ExternalLinkIcon,
    EyeIcon,
    Icon,
    IconButton,
    type IconType,
    LabelRegular,
    SuperscriptEmphasized,
    type TextProps,
    TrashIcon,
    UserIcon,
    UsersIcon,
    XIcon,
    YStack,
} from 'smoothie';

import { JOINED_DATA_SEPARATOR } from '../../entities/constants';
import {
    type GuideDetails,
    type MembershipDetails,
    type MembershipSummary,
    MembershipType,
} from '../../entities/entityTypes';
import type { Cents } from '../types/Common';
import { centsToFormattedDollars, formatTenure } from '../utils/formatting';

export const MonospaceDetailText = ({ children, ...props }: TextProps) => (
    <SuperscriptEmphasized
        color="$gray900"
        style={{ fontFamily: 'ui-monospace, Menlo, Monaco' }}
        {...props}
    >
        {children}
    </SuperscriptEmphasized>
);

export const MonospaceNumericText = ({ children, ...props }: TextProps) => (
    <BodyMediumRegular
        style={{ fontFamily: 'ui-monospace, Menlo, Monaco' }}
        {...props}
    >
        {children}
    </BodyMediumRegular>
);

/** Placeholder for empty or unavailable table cell values. */
export const MutedTableCell = () => (
    <BodySmallRegular color="$gray500">—</BodySmallRegular>
);

export const MonospaceCellRenderer = memo(
    (props: CustomCellRendererProps<unknown, string, unknown>) => {
        return <MonospaceDetailText>{props.value}</MonospaceDetailText>;
    },
);

export const TenureCellRenderer = memo(
    ({
        value,
    }: CustomCellRendererProps<MembershipSummary, string, unknown>) => {
        const createdAtString = value;
        const tenure = useMemo(() => {
            return formatTenure(createdAtString);
        }, [createdAtString]);

        return tenure.isNew ? (
            <Badge value="NEW" />
        ) : (
            <SuperscriptEmphasized>{tenure.value}</SuperscriptEmphasized>
        );
    },
);

export const MembershipActiveCellRenderer = memo(
    (props: CustomCellRendererProps<MembershipSummary, boolean, unknown>) => {
        return (
            <Icon
                icon={props.value ? CheckIcon : XIcon}
                color={props.value ? '$gray1000' : '$red1000'}
            />
        );
    },
);

export const MembershipTypeCellRenderer = memo(
    (
        props: CustomCellRendererProps<
            MembershipSummary,
            MembershipType,
            unknown
        >,
    ) => {
        const isJoint = props.value === MembershipType.JOINT;

        return (
            <YStack justifyContent="center">
                <Icon
                    icon={isJoint ? UsersIcon : UserIcon}
                    size={18}
                    color="$gray1000"
                />
            </YStack>
        );
    },
);

export const MemberInfoCellRenderer = memo(
    (props: CustomCellRendererProps<MembershipSummary, string, unknown>) => {
        const memberInfoString = props.value ?? '';
        const memberInfoArr = memberInfoString.split(JOINED_DATA_SEPARATOR);
        return (
            <YStack height="100%" justifyContent="center">
                {memberInfoArr.map((line, i) => (
                    <BodySmallRegular key={i}>{line.trim()}</BodySmallRegular>
                ))}
            </YStack>
        );
    },
);

export const MonospaceMemberInfoCellRenderer = memo(
    (props: CustomCellRendererProps<MembershipSummary, string, unknown>) => {
        const memberInfoString = props.value ?? '';
        const memberInfoArr = memberInfoString.split(JOINED_DATA_SEPARATOR);
        return (
            <YStack height="100%" justifyContent="center">
                {memberInfoArr.map((line, i) => (
                    <MonospaceDetailText key={i}>
                        {line.trim()}
                    </MonospaceDetailText>
                ))}
            </YStack>
        );
    },
);

export const GuideChipRenderer = (membershipDetails: MembershipDetails) => {
    const { guide } = membershipDetails;

    if (guide == null) {
        return <LabelRegular color="$gray900">No guide</LabelRegular>;
    }

    return (
        <Chip
            label={guide.firstName}
            image={guide.profileImageUrl ?? undefined}
        />
    );
};

export const GuideChipCellRenderer = memo(
    ({ guide }: { guide: GuideDetails | undefined }) => {
        if (guide == null) {
            return (
                <LabelRegular color="$gray900" marginLeft={24}>
                    No guide
                </LabelRegular>
            );
        }

        return (
            <YStack
                height="100%"
                justifyContent="center"
                style={{ transform: 'scale(0.8)' }}
            >
                <Chip
                    label={guide.firstName}
                    image={guide.profileImageUrl ?? undefined}
                />
            </YStack>
        );
    },
);

export const CentsToFormattedDollarsRenderer = (cents?: Cents | null) => {
    return (
        <MonospaceNumericText>
            {cents != null ? centsToFormattedDollars(cents) : ''}
        </MonospaceNumericText>
    );
};

interface DeleteItemRendererProps {
    onDelete: () => void;
    confirmDialogProps?: ConfirmDialogProps;
}

export const DeleteItemRenderer = ({
    onDelete,
    confirmDialogProps,
}: DeleteItemRendererProps) => {
    return (
        <YStack justifyContent="center" alignItems="center">
            <IconButton
                variant="tertiary"
                tone="danger"
                icon={TrashIcon}
                onPress={() => {
                    confirmDialog({
                        header: 'Are you sure?',
                        acceptLabel: 'Yes',
                        rejectLabel: 'Cancel',
                        accept: () => {
                            onDelete();
                        },
                        visible: true,
                        ...confirmDialogProps,
                    });
                }}
            />
        </YStack>
    );
};

interface EditItemRendererProps {
    onPress: () => void;
}

export const EditItemRenderer = ({ onPress }: EditItemRendererProps) => {
    return (
        <YStack justifyContent="center" alignItems="center">
            <IconButton variant="tertiary" icon={EditIcon} onPress={onPress} />
        </YStack>
    );
};

interface ViewItemRendererProps {
    onPress: () => void;
    icon?: IconType;
}

export const ViewItemRenderer = ({ onPress, icon }: ViewItemRendererProps) => {
    return (
        <YStack justifyContent="center" alignItems="center">
            <IconButton
                variant="tertiary"
                icon={icon ?? EyeIcon}
                onPress={onPress}
            />
        </YStack>
    );
};

interface DuplicateItemRendererProps {
    onPress: () => void;
}

export const DuplicateItemRenderer = ({
    onPress,
}: DuplicateItemRendererProps) => {
    return (
        <YStack justifyContent="center" alignItems="center">
            <IconButton variant="tertiary" icon={CopyIcon} onPress={onPress} />
        </YStack>
    );
};

interface UrlLinkRendererProps {
    url: string | null | undefined;
    linkText?: string;
}

export const UrlLinkRenderer = ({ url, linkText }: UrlLinkRendererProps) => {
    if (url == null || url === '') return '';

    return (
        <Link
            href={url as Href}
            target="_blank"
            style={{
                color: '#007bff',
                display: 'flex',
                alignItems: 'center',
            }}
        >
            <BodyMediumRegular color="#007bff" marginRight={4}>
                {linkText ?? url}
            </BodyMediumRegular>
            <Icon icon={ExternalLinkIcon} size={12} stringColor="#007bff" />
        </Link>
    );
};
